import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import icono from '../../../../assets/logos/Logo_Gold_black.png';
import Menu from './Menu';
import './Header.css';
import './Sidebar.css';
import MiniCart from './Minicart';
import ProfileOptions from './components/ProfileOptions';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faCartShopping, faBars, faWallet, faGift, faTicket } from '@fortawesome/free-solid-svg-icons';
import NotificationBell from '../../../../components/notifications/NotificationBell';
import { useCart } from '../../../../context/cartContext';
import { useAuth } from '../../../../context/authContext';
import { formatCurrency } from '../../../../services/api';
import { useTheme } from '../../../../context/themeContext';
import logoAlt from '../../../../assets/logos/Logo_Gold.png';
import useRechargeFlow from '../../../../hooks/useRechargeFlow';
import { useBonuses } from '../../../../context/bonusContext';
import BonusCelebrationModal from '../../../../components/bonus/BonusCelebrationModal';
import { DURATION, EASE, SPRING } from '../../../../utils/motion';

function Header() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { items, isOpen, openCart } = useCart();
  const { isAuthenticated, user, profile } = useAuth();
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openRecharge, rechargeModals } = useRechargeFlow();
  const { availableCount: bonusCount } = useBonuses();

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (y) => {
    const next = y > 8;
    setScrolled((prev) => (prev === next ? prev : next));
  });

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const balance = user?.balance?.amount || profile?.balance?.amount || 0;
  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';
  const itemCount = items.length;
  const logoSrc = theme === 'dark' ? logoAlt : icono;
  const isCarrito = pathname === '/carrito';
  const showUserInfo = Boolean(isAuthenticated && user);

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
      <div className="header__row header__top">
        <div className="header__logo">
          <button
            type="button"
            className="hamburger-icon"
            onClick={toggleSidebar}
            aria-label="Abrir menú"
            aria-expanded={sidebarOpen}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
          <Link to="/welcome" className="header__logo-link">
            <img src={logoSrc} alt="Gold Loterías" className="logotipo" />
          </Link>
        </div>

        <div className="header__trailing">
          <div className="header__mobile-bar">
            {showUserInfo && (
              <div className="header__user-info">
                <div className="header__balance">
                  <span className="balance-label">Saldo</span>
                  <span className="balance-value">{formatCurrency(balance)}</span>
                </div>
                {bonusCount > 0 && (
                  <Link
                    to="/perfil"
                    state={{ section: 'bonos' }}
                    className="header__bonus-badge"
                    aria-label={`Tienes ${bonusCount} ${bonusCount === 1 ? 'bono disponible' : 'bonos disponibles'}. Ver Mis bonos`}
                    title="Mis bonos"
                  >
                    <FontAwesomeIcon icon={faGift} aria-hidden="true" />
                    <span className="header__bonus-count">{bonusCount}</span>
                  </Link>
                )}
                <button
                  type="button"
                  className="header__recharge-btn"
                  onClick={openRecharge}
                  aria-label="Recargar saldo"
                >
                  <FontAwesomeIcon icon={faWallet} aria-hidden="true" />
                  <span className="header__recharge-text">Recargar</span>
                </button>
                <button
                  type="button"
                  className="header__recharge-btn header__tickets-btn"
                  onClick={() => navigate('/perfil', { state: { section: 'boletos' } })}
                  aria-label="Ver mis tickets"
                >
                  <FontAwesomeIcon icon={faTicket} aria-hidden="true" />
                  <span className="header__recharge-text">Mis tickets</span>
                </button>
                <div className="header__user-name">
                  <span className="user-first-name">{firstName}</span>
                  <span className="user-last-name">{lastName}</span>
                </div>
              </div>
            )}

            <div className="header__icons header__icons--actions">
              {showUserInfo && <NotificationBell />}
              <ProfileOptions />
              <button
                type="button"
                className={`car-icon ${isCarrito ? 'car-icon--active' : ''}`}
                onClick={() => openCart()}
                aria-label={`Carrito${itemCount ? `, ${itemCount} items` : ''}`}
              >
                <FontAwesomeIcon icon={faCartShopping} />
                {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
              </button>
            </div>
          </div>

          <div className="header__icons header__icons--theme">
            <ThemeToggle inline />
          </div>
        </div>
      </div>

      <div className={`header__row header__bottom ${sidebarOpen ? 'hidden' : ''}`}>
        <Menu className="header__menu__nav" />
      </div>

      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              key="overlay"
              className="overlay"
              onClick={closeSidebar}
              role="presentation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: DURATION.normal, ease: EASE.out }}
            />
            <motion.div
              key="sidebar"
              className="sidebar open"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%', transition: { duration: DURATION.normal, ease: EASE.inOut } }}
              transition={SPRING.soft}
            >
              <div className="sidebar__header">
                <button
                  type="button"
                  className="sidebar__close"
                  onClick={closeSidebar}
                  aria-label="Cerrar menú"
                >
                  <FontAwesomeIcon icon={faX} />
                </button>
              </div>
              <Menu className="sidebar__menu" onNavigate={closeSidebar} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {isOpen && <MiniCart />}
      {rechargeModals}
      <BonusCelebrationModal />
    </header>
  );
}

export default Header;
